import { useEffect, useState } from "react";
import API from "../../../services/api";
import "./staff.css";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

export default function StaffSalary() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const today = new Date();
  const [month, setMonth] = useState(MONTHS[today.getMonth()]);
  const [year,  setYear]  = useState(today.getFullYear());

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const yearsOptions = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  /* ── load ── */
  const loadSalary = async () => {
    try {
      setLoading(true);
      const res = await API.get("/school-admin/staff-salary/", {
        headers: { "X-SCHOOL-ID": school.school.id },
        params:  { month, year },
      });
      setRecords(res.data.results || []);
    } catch (err) { console.error(err); setRecords([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSalary(); }, [month, year]);

  /* ── toggle paid / unpaid ── */
  const toggleStatus = async (id, current) => {
    const next = current === "paid" ? "unpaid" : "paid";
    try {
      await API.post(
        `/school-admin/staff-salary/set-status/${id}/`,
        { status: next },
        { headers: { "X-SCHOOL-ID": school.school.id } },
      );
      loadSalary();
    } catch (err) { console.error(err); alert("Error updating salary status"); }
  };

  /* ── invoice PDF ── */
  const downloadInvoice = (id) => {
    const url  = `${API.defaults.baseURL}school-admin/staff-salary/invoice/${id}/?school_id=${school.school.id}`;
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `salary_invoice_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  /* ── summary ── */
  const paidCount   = records.filter(r => r.status === "paid").length;
  const unpaidCount = records.filter(r => r.status === "unpaid").length;
  const totalPaid   = records.filter(r => r.status === "paid").reduce((s, r) => s + Number(r.salary || 0), 0);

  return (
    <div className="st-page">

      {/* ══ HERO ══ */}
      <div className="st-hero">
        <div className="st-hero-left">
          <div className="st-hero-icon">💰</div>
          <div>
            <h2 className="st-hero-title">Staff Salary</h2>
            <p className="st-hero-sub">Monthly salary management for non-teaching staff</p>
          </div>
        </div>
        <div className="st-hero-right" style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "rgba(255,255,255,.75)", fontSize: "13px", fontWeight: 700 }}>
            ✅ {paidCount} paid &nbsp;·&nbsp; ⏳ {unpaidCount} pending
          </span>
        </div>
      </div>

      {/* ══ SALARY FILTER ══ */}
      <div className="st-salary-filter">
        <span className="st-filter-label">Filter by</span>
        <div className="st-salary-filter-group">
          <label>Month</label>
          <select value={month} onChange={e => setMonth(e.target.value)}>
            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="st-salary-filter-group">
          <label>Year</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {yearsOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {totalPaid > 0 && (
          <span style={{
            marginLeft: "auto", background: "#dcfce7", color: "#166534",
            padding: "7px 16px", borderRadius: "20px",
            fontSize: "13px", fontWeight: 800,
          }}>
            ₹ {totalPaid.toLocaleString()} disbursed
          </span>
        )}
      </div>

      {/* ══ TABLE ══ */}
      <div className="st-card">
        {loading ? (
          <div className="st-empty">
            <div className="st-empty-icon">⏳</div>
            <strong>Loading salary data…</strong>
          </div>
        ) : records.length === 0 ? (
          <div className="st-empty">
            <div className="st-empty-icon">📭</div>
            <strong>No salary records found</strong>
            <p>No staff records for {month} {year}</p>
          </div>
        ) : (
          <table className="st-table">
            <thead>
              <tr>
                <th>Staff Member</th>
                <th>Role</th>
                <th>Month / Year</th>
                <th>Salary</th>
                <th>Status</th>
                <th>Mark</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.id}>
                  <td>
                    <div className="st-name">{r.staff_name}</div>
                  </td>
                  <td>
                    <div className="st-role">{r.role || "—"}</div>
                  </td>
                  <td style={{ color: "#64748b", fontSize: "13px" }}>
                    {r.month} {r.year}
                  </td>
                  <td style={{ fontWeight: 800, fontSize: "15px", color: "#1e293b" }}>
                    ₹ {Number(r.salary || 0).toLocaleString()}
                  </td>
                  <td>
                    <span className={`st-pill ${r.status === "paid" ? "paid" : "unpaid"}`}>
                      {r.status === "paid" ? "✓ Paid" : "⏳ Unpaid"}
                    </span>
                  </td>
                  <td>
                    {r.status === "paid"
                      ? <button className="st-btn-mark-unpaid" onClick={() => toggleStatus(r.id, r.status)}>
                          Mark Unpaid
                        </button>
                      : <button className="st-btn-mark-paid" onClick={() => toggleStatus(r.id, r.status)}>
                          Mark Paid
                        </button>
                    }
                  </td>
                  <td>
                    {r.status === "paid"
                      ? <button className="st-btn-invoice" onClick={() => downloadInvoice(r.id)}>
                          📄 Invoice
                        </button>
                      : <span style={{ color: "#cbd5e1", fontSize: "13px" }}>—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}
